/* Componenti condivisi dalle pagine delle funzioni avanzate, nello stile di KALYX. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Search, X } from "lucide-react";
import { botGet, botPut, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DiscordEmbedPreview } from "@/components/DiscordEmbedPreview";

export const labelC = "mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]";
export const inputC = "border-[#232838] bg-[#131620] text-white placeholder:text-[#5f6479] focus-visible:ring-[#5865F2]";
export const primaryBtn = "bg-[#5865F2] font-semibold text-white hover:bg-[#4752C4]";
export const outlineBtn = "border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D] hover:text-white";
export const dangerBtn = "bg-[#F23F43] font-semibold text-white hover:bg-[#d83236]";

/**
 * Riquadro «Come funziona»: spiega una pagina in parole semplici, con passi numerati ed esempio.
 * Si può chiudere; la scelta resta memorizzata nel browser (id = chiave).
 */
export function HowItWorks({ id, title = "Come funziona", intro, steps = [], example, tips = [] }) {
  const key = `kalyx-help-${id}`;
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(key) !== "0"; } catch { return true; } });
  const toggle = () => { const n = !open; setOpen(n); try { localStorage.setItem(key, n ? "1" : "0"); } catch { /* storage non disponibile */ } };
  return (
    <div className="overflow-hidden rounded-2xl border border-[#00F0FF]/20 bg-gradient-to-br from-[#00F0FF]/[0.07] via-[#5865F2]/[0.06] to-transparent" data-testid={`help-${id}`}>
      <button onClick={toggle} className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#00F0FF]/15 text-sm font-black text-[#00F0FF]">?</span>
        <span className="flex-1 text-sm font-bold text-white">{title}</span>
        <span className="text-xs font-semibold text-[#7fe9f5]">{open ? "Nascondi" : "Mostra"}</span>
      </button>
      {open && (
        <div className="space-y-3 px-4 pb-4 text-sm text-[#C9CDD4]">
          {intro && <p>{intro}</p>}
          {steps.length > 0 && (
            <ol className="grid gap-2 md:grid-cols-3">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-2.5 rounded-xl border border-white/5 bg-[#0f111a]/60 p-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#5865F2] text-xs font-bold text-white">{i + 1}</span>
                  <span className="leading-snug">{s}</span>
                </li>
              ))}
            </ol>
          )}
          {example && <p className="rounded-xl border border-[#FEE75C]/15 bg-[#FEE75C]/[0.06] px-3 py-2 text-[#f5e6a0]"><b className="text-[#FEE75C]">Esempio: </b>{example}</p>}
          {tips.length > 0 && (
            <ul className="space-y-1">
              {tips.map((t, i) => <li key={i} className="flex gap-2"><span className="text-[#00F0FF]">✦</span><span>{t}</span></li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Spiegazione breve in cima a una scheda: cosa fa + esempio. */
export function TabIntro({ children, example }) {
  return (
    <div className="rounded-xl border border-[#232838] bg-[#131620] px-4 py-3 text-sm text-[#C9CDD4]">
      {children}
      {example && <span className="mt-1 block text-[#949BA4]"><b className="text-[#B5BAC1]">Esempio:</b> {example}</span>}
    </div>
  );
}

export function PageHeader({ title, subtitle, icon: Icon, actions }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex items-center gap-4">
        {Icon && (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#5865F2] to-[#8B5CF6] shadow-[0_8px_24px_-8px_rgba(88,101,242,0.8)]">
            <Icon className="h-6 w-6 text-white" />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-[#949BA4]">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, desc, icon: Icon, right, children, className = "", testId }) {
  return (
    <Card data-testid={testId} className={`card-elevated border-[#232838] bg-[#161923] p-5 ${className}`}>
      {(title || right) && (
        <div className="mb-4 flex items-start gap-3">
          {Icon && <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#5865F2]/15"><Icon className="h-5 w-5 text-[#8ea0ff]" /></div>}
          <div className="min-w-0 flex-1">
            {title && <p className="font-bold text-white">{title}</p>}
            {desc && <p className="text-xs text-[#949BA4]">{desc}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </Card>
  );
}

export function Field({ label, hint, children }) {
  return (
    <div className="min-w-0">
      {label && <label className={labelC}>{label}</label>}
      {children}
      {hint && <p className="mt-1 text-[11px] text-[#949BA4]">{hint}</p>}
    </div>
  );
}

export function ToggleRow({ title, desc, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-[#232838] bg-[#131620] px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white">{title}</p>
        {desc && <p className="text-xs text-[#949BA4]">{desc}</p>}
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

export function NumberInput({ value, onChange, min = 0, max = 1e9, step = 1, suffix, className = "w-24" }) {
  return (
    <div className="flex items-center gap-2">
      <Input type="number" min={min} max={max} step={step} value={value ?? 0}
        onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))} className={`${inputC} ${className}`} />
      {suffix && <span className="text-sm text-[#949BA4]">{suffix}</span>}
    </div>
  );
}

export function TextInput({ value, onChange, ...rest }) {
  return <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputC} {...rest} />;
}

export function TextArea({ value, onChange, rows = 3, ...rest }) {
  return <Textarea value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)} className={inputC} {...rest} />;
}

/** Lista "una voce per riga" che non perde gli a capo mentre si scrive. */
export function LinesInput({ value = [], onChange, rows = 4, placeholder }) {
  const [text, setText] = useState(value.join("\n"));
  const clean = (t) => t.split("\n").map((w) => w.trim()).filter(Boolean);
  useEffect(() => {
    setText((cur) => (clean(cur).join("\n") === value.join("\n") ? cur : value.join("\n")));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Textarea rows={rows} value={text} placeholder={placeholder} className={inputC} onChange={(e) => { setText(e.target.value); onChange(clean(e.target.value)); }} />;
}

export function SimpleSelect({ value, onChange, options, placeholder = "Seleziona", className = "", testId }) {
  return (
    <Select value={value === null || value === undefined ? "" : String(value)} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={`${inputC} ${className}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72 border-[#232838] bg-[#161923] text-[#DBDEE1]">
        {options.map((o) => <SelectItem key={String(o.value)} value={String(o.value)} className="focus:bg-[#252B3D]">{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

/** Schede a pillola, come quelle di Canali/Ruoli. */
export function PillTabs({ tabs, active, onChange }) {
  return (
    <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-2xl border border-[#232838] bg-[#12141D] p-1">
      {tabs.map((t) => (
        <button key={t.id} onClick={() => onChange(t.id)} data-testid={`tab-${t.id}`}
          className={`flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold transition-all ${
            active === t.id ? "bg-gradient-to-r from-[#5865F2] to-[#7c6cf6] text-white shadow-[0_6px_18px_-8px_rgba(88,101,242,0.9)]" : "text-[#949BA4] hover:bg-white/5 hover:text-white"}`}>
          {t.icon && <t.icon className="h-4 w-4" />}{t.label}
          {t.dot !== undefined && <span className={`h-2 w-2 rounded-full ${t.dot ? "bg-[#23A55A] shadow-[0_0_6px_#23A55A]" : "bg-[#4b4d54]"}`} />}
        </button>
      ))}
    </div>
  );
}

export function Empty({ icon: Icon, title, children }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#232838] px-6 py-12 text-center">
      {Icon && <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#5865F2]/10"><Icon className="h-6 w-6 text-[#8ea0ff]" /></div>}
      <p className="font-semibold text-white">{title}</p>
      {children && <p className="mt-1 max-w-md text-sm text-[#949BA4]">{children}</p>}
    </div>
  );
}

export function Loading() {
  return <div className="flex items-center justify-center py-16 text-[#949BA4]"><Loader2 className="mr-2 h-5 w-5 animate-spin text-[#5865F2]" />Caricamento…</div>;
}

export function StatTile({ icon: Icon, value, label, tone = "#5865F2" }) {
  return (
    <Card className="card-elevated flex items-center gap-4 border-[#232838] bg-[#161923] p-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${tone}22`, color: tone }}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <p className="text-2xl font-extrabold tracking-tight text-white">{value}</p>
        <p className="truncate text-xs text-[#949BA4]">{label}</p>
      </div>
    </Card>
  );
}

const TONES = {
  blue: "border-[#5865F2]/30 bg-[#5865F2]/15 text-[#a5b0ff]",
  green: "border-[#23A55A]/30 bg-[#23A55A]/15 text-[#57d38c]",
  red: "border-[#F23F43]/30 bg-[#F23F43]/15 text-[#ff7a7d]",
  yellow: "border-[#FEE75C]/30 bg-[#FEE75C]/10 text-[#FEE75C]",
  gray: "border-white/10 bg-white/5 text-[#B5BAC1]",
};
export function Pill({ tone = "gray", children }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONES[tone]}`}>{children}</span>;
}

/** Barra "Salva" fissa in fondo alla pagina. */
export function SaveBar({ feature, prepare }) {
  if (!feature.dirty) return null;
  return (
    <div className="sticky bottom-4 z-20 flex items-center gap-3 rounded-2xl border border-[#5865F2]/40 bg-[#12141D]/95 px-4 py-3 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)] backdrop-blur animate-fade-up">
      <span className="flex-1 text-sm font-semibold text-[#FEE75C]">Hai modifiche non salvate</span>
      <Button variant="ghost" onClick={feature.reset} className="text-[#B5BAC1] hover:bg-white/5 hover:text-white">Annulla</Button>
      <Button onClick={() => feature.save(prepare)} disabled={feature.saving} className={primaryBtn} data-testid="save-bar-save">
        {feature.saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salva
      </Button>
    </div>
  );
}

/** Carica, modifica e salva una funzione del bot (GET/PUT /features/:name). */
export function useFeature(name) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    setDraft(null);
    return botGet(`features/${name}`).then(setSaved).catch((e) => setError(apiError(e)));
  }, [name]);
  useEffect(() => { setSaved(null); reload(); }, [reload]);
  const value = draft ?? saved;
  const change = (patch) => setDraft((d) => ({ ...(d ?? saved), ...patch }));
  const save = async (prepare = (v) => v) => {
    setSaving(true);
    try {
      const r = await botPut(`features/${name}`, prepare(structuredClone(value)));
      setSaved(r);
      setDraft(null);
      toast.success("Impostazioni salvate", { description: "Sono già attive sul server." });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  return { value, change, save, reload, saving, dirty: !!draft, reset: () => setDraft(null), loading: !saved && !error, error };
}

export function FeatureGate({ feature, children }) {
  if (feature.error) return <p className="rounded-xl border border-[#F23F43]/30 bg-[#F23F43]/10 p-4 text-sm text-[#ff7a7d]">{feature.error}</p>;
  if (feature.loading) return <Loading />;
  return children(feature.value);
}

/** Più ruoli: pillole dei selezionati + menu per aggiungerne. */
export function RoleMulti({ value = [], onChange }) {
  const { roles } = useData();
  const list = roles.filter((r) => !r.isEveryone && !r.managed);
  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const r = roles.find((x) => x.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-[#232838] bg-[#131620] py-1 pl-2 pr-1 text-xs text-white">
                <span className="h-2 w-2 rounded-full" style={{ background: r?.color || "#99AAB5" }} />{r?.name || "ruolo eliminato"}
                <button onClick={() => onChange(value.filter((v) => v !== id))} className="rounded-full p-0.5 text-[#949BA4] hover:bg-white/10 hover:text-white"><X className="h-3 w-3" /></button>
              </span>
            );
          })}
        </div>
      )}
      <SimpleSelect value="" placeholder="+ Aggiungi ruolo" onChange={(id) => id && !value.includes(id) && onChange([...value, id])}
        options={list.filter((r) => !value.includes(r.id)).map((r) => ({ value: r.id, label: r.name }))} />
    </div>
  );
}

/** Più canali (anche categorie). */
export function ChannelMulti({ value = [], onChange, types = ["text", "announcement", "forum", "voice", "category"] }) {
  const { channels } = useData();
  const list = channels.filter((c) => types.includes(c.type));
  const icon = (t) => ({ voice: "🔊 ", category: "📁 ", announcement: "📢 ", forum: "🗂️ " }[t] || "# ");
  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const c = channels.find((x) => x.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full border border-[#232838] bg-[#131620] py-1 pl-2.5 pr-1 text-xs text-white">
                {c ? icon(c.type) + c.name : "canale eliminato"}
                <button onClick={() => onChange(value.filter((v) => v !== id))} className="rounded-full p-0.5 text-[#949BA4] hover:bg-white/10 hover:text-white"><X className="h-3 w-3" /></button>
              </span>
            );
          })}
        </div>
      )}
      <SimpleSelect value="" placeholder="+ Aggiungi canale" onChange={(id) => id && !value.includes(id) && onChange([...value, id])}
        options={list.filter((c) => !value.includes(c.id)).map((c) => ({ value: c.id, label: icon(c.type) + c.name }))} />
    </div>
  );
}

/** Cerca un membro per nome. */
export function MemberSearch({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q.trim()) return setResults([]);
    const t = setTimeout(() => botGet("members", { query: q.trim(), limit: 8 }).then((r) => setResults(r.members || [])).catch(() => setResults([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[#232838] bg-[#131620] px-2 py-1.5">
        {value.avatar && <img src={value.avatar} alt="" className="h-6 w-6 rounded-full object-cover" />}
        <span className="flex-1 truncate text-sm font-semibold text-white">{value.displayName}</span>
        <button onClick={() => onChange(null)} className="rounded p-1 text-[#949BA4] hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#949BA4]" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un membro per nome…" className={`${inputC} pl-9`} />
      {results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-[#232838] bg-[#161923] shadow-2xl">
          {results.map((m) => (
            <button key={m.id} onClick={() => { onChange(m); setQ(""); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white hover:bg-[#252B3D]">
              {m.avatar && <img src={m.avatar} alt="" className="h-6 w-6 rounded-full object-cover" />}{m.displayName}
              <span className="ml-auto text-xs text-[#949BA4]">@{m.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Editor di messaggio compatto: testo + un embed (titolo, descrizione, colore, immagine) con anteprima Discord.
 * Per messaggi più complessi c'è l'Embed Builder.
 */
export function MessageMini({ value, onChange, hint }) {
  const msg = value || {};
  const embed = msg.embeds?.[0];
  const setEmbed = (patch) => onChange({ ...msg, embeds: [{ ...(embed || { color: "#5865F2" }), ...patch }] });
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <Field label="Testo" hint={hint}><TextArea value={msg.content} onChange={(content) => onChange({ ...msg, content })} rows={3} placeholder="Testo del messaggio (facoltativo se c'è l'embed)" /></Field>
        {embed ? (
          <div className="space-y-3 rounded-xl border border-[#232838] bg-[#12141D] p-3" style={{ borderLeft: `4px solid ${embed.color || "#5865F2"}` }}>
            <div className="flex items-center justify-between">
              <span className={labelC + " mb-0"}>Embed</span>
              <button onClick={() => onChange({ ...msg, embeds: [] })} className="text-xs font-semibold text-[#F23F43] hover:underline">Rimuovi</button>
            </div>
            <TextInput value={embed.title} onChange={(title) => setEmbed({ title })} placeholder="Titolo" />
            <TextArea value={embed.description} onChange={(description) => setEmbed({ description })} rows={3} placeholder="Descrizione (supporta **grassetto**, *corsivo*…)" />
            <div className="flex gap-2">
              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(embed.color || "") ? embed.color : "#5865F2"} onChange={(e) => setEmbed({ color: e.target.value })} className="h-9 w-12 cursor-pointer rounded-md border border-[#232838] bg-transparent" />
              <TextInput value={embed.image} onChange={(image) => setEmbed({ image })} placeholder="URL immagine (facoltativo)" />
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setEmbed({ title: "", description: "" })} className={outlineBtn}>+ Aggiungi embed</Button>
        )}
      </div>
      <div>
        <p className={labelC}>Anteprima</p>
        <DiscordEmbedPreview message={msg} />
      </div>
    </div>
  );
}

/** Toglie campi vuoti dal messaggio prima di inviarlo al bot. */
export function cleanMessage(m) {
  const out = {};
  if (m?.content?.trim()) out.content = m.content;
  const embeds = (m?.embeds || [])
    .map((e) => Object.fromEntries(Object.entries(e).filter(([, v]) => v !== "" && v !== null && v !== undefined)))
    .filter((e) => Object.keys(e).some((k) => k !== "color"));
  if (embeds.length) out.embeds = embeds;
  if (m?.buttons?.length) out.buttons = m.buttons;
  return out;
}

export const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 45) return "adesso";
  if (s < 3600) return `${Math.round(s / 60)} min fa`;
  if (s < 86400) return `${Math.round(s / 3600)} h fa`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)} g fa`;
  return new Date(iso).toLocaleDateString("it-IT");
};
export const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "—");
export const formatDuration = (ms) => {
  if (!ms) return "permanente";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return [d && `${d} ${d === 1 ? "giorno" : "giorni"}`, h && `${h} ${h === 1 ? "ora" : "ore"}`, !d && m && `${m} min`].filter(Boolean).slice(0, 2).join(" ") || "meno di un minuto";
};
